import { toast } from 'sonner';

/**
 * Utilities to deduplicate toasts per item/field using stable IDs.
 * Callers should use the same id for a given item+field to prevent stacking.
 */

// Maintain lightweight state for smooth transitions
type ToastState = { lastStart?: number; timeout?: any };
const toastStates = new Map<string, ToastState>();

const MIN_LOADING_MS = 600; // ensure loading shows briefly to avoid flicker

export function makeUpdateToastId(scope: string, id: string | number, field?: string) {
  return `${scope}:${String(id)}:${field || 'all'}`;
}

function scheduleAfterMin(id: string, fn: () => void) {
  const st = toastStates.get(id);
  const last = st?.lastStart ?? 0;
  const elapsed = last ? Date.now() - last : MIN_LOADING_MS;
  const delay = Math.max(0, MIN_LOADING_MS - elapsed);
  if (delay > 0) {
    const state = st || {};
    if (state.timeout) clearTimeout(state.timeout);
    state.timeout = setTimeout(() => {
      fn();
      state.timeout = undefined;
      toastStates.delete(id);
    }, delay);
    toastStates.set(id, state);
  } else {
    fn();
    toastStates.delete(id);
  }
}

export function dedupSuccess(id: string, message: string, duration = 1200) {
  scheduleAfterMin(id, () => toast.success(message, { id, duration }));
}

export function dedupError(id: string, message: string, duration = 1800) {
  scheduleAfterMin(id, () => toast.error(message, { id, duration }));
}

export function dedupInfo(id: string, message: string, duration = 1500) {
  toast.info(message, { id, duration });
}

export function dedupLoading(id: string, message = 'Saving…') {
  // Update/create persistent loading toast and mark start time
  const st = toastStates.get(id) || {};
  st.lastStart = Date.now();
  if (st.timeout) {
    clearTimeout(st.timeout);
    st.timeout = undefined;
  }
  toast.loading(message, { id, duration: Infinity });
  toastStates.set(id, st);
}

export function dedupDismiss(id: string) {
  try {
    toast.dismiss(id);
  } finally {
    const st = toastStates.get(id);
    if (st?.timeout) clearTimeout(st.timeout);
    toastStates.delete(id);
  }
}
